
















































































































































































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';

// prime-vue
import Listbox from 'primevue/listbox';
import Panel from 'primevue/panel';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import ProgressSpinner from 'primevue/progressspinner';
import InputSwitch from 'primevue/inputswitch';
import Chip from 'primevue/chip';

// hooks
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { useGeoRecordModify } from '@/vue3/hooks/geoRecordModifyHook';

// services
import { useService } from '@/vue3/api/services';

// components
import MultiEditTool from '@/vue3/components/geo-record-tools/MultiEditTool.vue';
import MultiModifyTool from '@/vue3/components/geo-record-tools/MultiModifyTool.vue';
import MultiArchiveEditTool from '@/vue3/components/geo-record-tools/MultiArchiveEditTool.vue';
import MultiDuplicateTool from '@/vue3/components/geo-record-tools/MultiDuplicateTool.vue';

// models
import { GeoRecord, GeoRecordCategorySummary } from '@/vue3/api/models';

// turf
import * as turf from '@turf/turf';
import { useNotify } from '@/vue3/hooks/notifyHook';

// config
import Tools from '@/vue3/configs/table/multiSelectToolConfig';

export default defineComponent({
    components: {
        Listbox,
        Panel,
        TabView,
        TabPanel,
        MultiEditTool,
        MultiModifyTool,
        ProgressSpinner,
        Chip,
        InputSwitch,
        MultiArchiveEditTool,
        MultiDuplicateTool,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
        setMapboxDrawSnap: {
            type: Function,
            default: () => {},            
        },
        role: {
            type: Object,
            default: () => {},
        },
        moveGeoRecordToArchive: {
            type: Function,
            default: () => {},
        },
        layerTree: {
            type: Object,
            default: null,
        },
    },
    setup(props: any, vm: any) {
        // services
        const {
            getGeoRecord,
            getGeoRecordCategorySummaryByRole,
            categoriesToArray,
            getArchiveGeoRecord,
            updateMultipleGeoRecords,
            getMultipleGeoRecords,
            createMultipleArchiveGeoRecord,
            getMultipleArchiveGeoRecords,
        } = useService();

        // hooks
        const {
            initMultiGeoRecordSource,
            removeCreateGeoRecordSource,
            removeSelectedGeoRecord,
            refreshGeoRecord,
            updateGeoRecordExtrusion,
            refreshGeoRecordExtrusion,
            initCursorSource,
            startDrawRectangle,
            removeGeoRecordArchiveExtrusionLayers,
            removeGeoRecordExtrusion,
            removeGeoRecord,
        } = useGeoRecord();

        const { updateModifiedGeoRecord } = useGeoRecordModify();

        const { successToast, errorToast } = useNotify();

        // refs
        const isGeomModificationActivated = ref<boolean>(false);
        const isGeoRecordListCreated = ref<boolean>(false);
        const isLoading = ref<boolean>(false);
        const isModifyAllowed = ref<boolean>(false);
        const isMultipleGeomSelected = ref<boolean>(false);
        const isDrawing = ref<boolean>(false);
        const isFreeDrawMode = ref<boolean>(false);
        const isArchiveRecordSelected = ref<boolean>(false);
        const isGeoRecordSelected = ref<boolean>(false);
        const isRecordSelected = ref<boolean>(false);

        const recordSource = ref(['geo-records', 'geo-records-extrusion']);

        const geoRecordsArray = ref<GeoRecord[]>([]);

        const categorySummaries = ref<GeoRecordCategorySummary[]>([]);

        const selectedGeoRecordNames = ref<string[]>([]);

        const features = ref<any>([]);
        const noOfClick = ref<any>();
        const activeTab = ref<any>();

        // methods
        const checkIsModified = (geoRecord: any): boolean => {
            let isAllowed = false;

            if (geoRecord.geoRecordCategoryId === 0) {
                return true;
            }

            for (vm of props.role.roleGeoRecordCategoryPermissionsLink) {
                if (vm.id === geoRecord.geoRecordCategoryId) {
                    if (vm.canEdit) isAllowed = true;
                    break;
                }
            }
            return isAllowed;
        };

        const onTabChange = (tab: any): void => {
            activeTab.value = tab.index;

            if (tab.index === 1) {
                isGeomModificationActivated.value = true;

                geoRecordsArray.value.forEach((geoRecord) => {
                    removeGeoRecord(props.mapbox.map, geoRecord);
                });

                removeCreateGeoRecordSource(props.mapbox.map);

                return;
            }

            props.mapbox.draw.changeMode('simple_select');
            props.mapbox.draw.deleteAll();

            isGeomModificationActivated.value = false;

            initMultiGeoRecordSource(props.mapbox.map, geoRecordsArray.value);
        };

        const clearGeoRecordSelection = (): void => {
            geoRecordsArray.value = [];
            selectedGeoRecordNames.value = [];
            isGeoRecordListCreated.value = false;
            isMultipleGeomSelected.value = false;
            isGeomModificationActivated.value = false;
            isFreeDrawMode.value = false;
            isArchiveRecordSelected.value = false;
            isGeoRecordSelected.value = false;
            isRecordSelected.value = false;
            recordSource.value = ['geo-records', 'geo-records-extrusion'];

            props.mapbox.draw.changeMode('simple_select');
            props.mapbox.draw.deleteAll();

            removeCreateGeoRecordSource(props.mapbox.map);
        };

        const prepareGeoRecordFormData = (
            geoRecords: any,
            isModification: any,
            limit: number,
        ) => {
            let formData = new FormData();

            if (geoRecords.length <= limit) {
                geoRecords.forEach((geoRecord, index) => {
                    formData.append(`json-${index}`, JSON.stringify(geoRecord));
                    if (geoRecord.archiveEventId !== null && geoRecord.images) {
                        geoRecord.images.forEach((geoRecordImage, i) => {
                            formData.append(
                                `image${index}-${i}`,
                                geoRecordImage.image,
                            );
                            const dateString = geoRecordImage.date
                                ? `${+geoRecordImage.date}`
                                : JSON.stringify(geoRecordImage.date);
                            const shortenedDateString = dateString.slice(
                                0,
                                dateString.length - 3,
                            );
                            formData.append(
                                `image-date${index}-${i}`,
                                shortenedDateString,
                            );
                        });
                    }
                });

                formData.append(
                    'isModification',
                    JSON.stringify(isModification),
                );
                return formData;
            } else {
                return errorToast(
                    `Cannot update more than ${limit} records in one request`,
                );
            }
        };

        const saveMultipleGeoRecords = async (geoRecords) => {
            isLoading.value = true;
            let updatedGeoRecords;

            const geoRecordIds = geoRecords.map((record) => record.id);
            const isAnyRecordArchived = geoRecords.some(
                (record: any) => record.archiveEventId !== null,
            );

            try {
                if (!isAnyRecordArchived) {
                    const formData = prepareGeoRecordFormData(
                        geoRecords,
                        false,
                        100,
                    );

                    if (formData) {
                        await updateMultipleGeoRecords(formData);

                        updatedGeoRecords = await getMultipleGeoRecords(
                            geoRecordIds,
                        );
                        for (const updatedGeoRecord of updatedGeoRecords) {
                            updateGeoRecordExtrusion(
                                props.mapbox.map,
                                updatedGeoRecord,
                            );
                            refreshGeoRecordExtrusion(
                                props.mapbox.map,
                                updatedGeoRecord,
                            );
                            refreshGeoRecord(
                                props.mapbox.map,
                                updatedGeoRecord,
                                true,
                            );
                        }

                        successToast('Successfully saved geo records.');

                        removeGeoRecordArchiveExtrusionLayers(props.mapbox.map);
                    }
                } else {
                    await moveMultipleGeoRecordsToArchive(geoRecords);
                }
            } catch (error) {
                console.log(error)
                props.mapbox.draw.deleteAll();
                clearGeoRecordSelection();
            }

            geoRecords.forEach((geoRecord) =>
                removeSelectedGeoRecord(props.mapbox.map, geoRecord),
            );
        };

        const moveMultipleGeoRecordsToArchive = async (geoRecords) => {
            const formData = prepareGeoRecordFormData(geoRecords, false, 100);

            if (formData) {
                geoRecords.forEach((geoRecord: any) => {
                    removeSelectedGeoRecord(props.mapbox.map, geoRecord);
                    removeGeoRecordExtrusion(props.mapbox.map, geoRecord);
                    removeGeoRecord(props.mapbox.map, geoRecord);
                });

                await createMultipleArchiveGeoRecord(formData);

                successToast('Geo Records successfully archived.');

                await props.layerTree.fetchArchiveEvents();
                await props.layerTree.refreshGeoRecordTree();
                await props.layerTree.refershArchiveGeoRecordLayerTree();
            }
        };

        const saveGeoRecords = async () => {
            const modifiedGeoRecords = geoRecordsArray.value.map((geoRecord) =>
                updateModifiedGeoRecord(
                    props.mapbox.draw,
                    geoRecord,
                    categorySummaries.value,
                ),
            );

            await saveMultipleGeoRecords(modifiedGeoRecords);

            clearGeoRecordSelection();

            isLoading.value = false;

            await props.layerTree.refreshGeoRecordTreeAndState([], false); //since its update no need to save in layertree
        };

        const getGeoRecordDetails = async (features: any): Promise<void> => {
            features.map(async (feature) => {
                if (
                    !geoRecordsArray.value.some(
                        (geoRecord) => geoRecord.id === feature.id,
                    )
                ) {
                    isLoading.value = true;

                    let geoRecord = await getGeoRecord(feature.id);

                    isModifyAllowed.value = checkIsModified(geoRecord);

                    if (!isModifyAllowed.value) return;

                    geoRecordsArray.value.push(geoRecord);

                    selectedGeoRecordNames.value.push(feature.properties);

                    isGeoRecordListCreated.value = true;
                    isMultipleGeomSelected.value = true;

                    initMultiGeoRecordSource(
                        props.mapbox.map,
                        geoRecordsArray.value,
                    );

                    isLoading.value = false;
                }
            });
        };

        const getMultipleGeoRecordDetails = async (features: any) => {
            let geoRecordIds: number[] = [];

            for (const feature of features) {
                if (
                    !geoRecordsArray.value.some(
                        (geoRecord) => geoRecord.id === feature.id,
                    )
                ) {
                    geoRecordIds.push(feature.id);
                }
            }

            let multiGeoRecords = await getMultipleGeoRecords(geoRecordIds);
            multiGeoRecords.forEach((geoRecord) => {
                isModifyAllowed.value = checkIsModified(geoRecord);
                geoRecordsArray.value.push(geoRecord);
            });

            for (const feature of features) {
                selectedGeoRecordNames.value.push(feature.properties);

                isGeoRecordListCreated.value = true;
                isMultipleGeomSelected.value = true;

                initMultiGeoRecordSource(
                    props.mapbox.map,
                    geoRecordsArray.value,
                );
            }

            isLoading.value = false;
        };

        const turnOffFreeDrawMode = (): void => {
            props.mapbox.map.off('click', activateFreeDrawMode);
            props.mapbox.map.off('click', onMapClick);
            props.mapbox.map.off('click', getFeatureDetails);
            props.mapbox.draw.delete('temp-rectangle');
            props.mapbox.draw.changeMode('simple_select');
        };

        const changeDrawMode = (): void => {
            if (!isFreeDrawMode.value) {
                turnOffFreeDrawMode();
                props.mapbox.map.on('click', onMultiGeoRecordClick);
                props.mapbox.map.on('touchend', onMultiGeoRecordClick);

                props.mapbox.map.on('contextmenu', onMultiGeoRecordClick);
            } else {
                activateFreeDrawMode();
                props.mapbox.map.off('click', onMultiGeoRecordClick);
            }
        };

        const onMultiGeoRecordClick = (e: any): void => {
            isMultipleGeomSelected.value = false;

            features.value = props.mapbox.map.queryRenderedFeatures(e.point);
            const featureSource = features.value[0].source;
            let geoRecord: any;

            if (
                recordSource.value.includes(featureSource) &&
                !isArchiveRecordSelected.value
            ) {
                isRecordSelected.value = true;
                geoRecord = features.value.find(
                    (feature: any) => feature.source === featureSource,
                );
                getGeoRecordDetails([geoRecord]);
                isGeoRecordSelected.value = true;
                isArchiveRecordSelected.value = false;
            } else if (
                recordSource.value.includes(featureSource) &&
                !isGeoRecordSelected.value
            ) {
                isRecordSelected.value = true;

                geoRecord = features.value.find(
                    (feature: any) => feature.source === featureSource,
                );

                getArchiveGeoRecordDetails([geoRecord]);
                isArchiveRecordSelected.value = true;
                isGeoRecordSelected.value = false;
            }
        };

        const activateCursor = (): void => {
            if (props.mapbox.map.getSource('create-cursor-point')) return;
            initCursorSource(props.mapbox.map);
        };

        const activateFreeDrawMode = (): void => {
            noOfClick.value = 0;
            isDrawing.value = false;

            activateCursor();

            startDrawRectangle(props.mapbox.draw);
            props.mapbox.map.on('click', onMapClick);
        };

        const onMapClick = () => {
            noOfClick.value++;

            if (isDrawing) {    //Don't change to isDrawing.value
                if (noOfClick.value === 1) {
                    props.mapbox.map.on('mousemove', getFeatureDetails);
                }

                if (noOfClick.value === 2) {
                    getGeoRecordsByFeature();

                    props.mapbox.map.off('mousemove', getFeatureDetails);
                    noOfClick.value = 0;
                }
            }
        };

        const getFeatureDetails = (e: any): void => {
            let bbox =
                props.mapbox.draw.get('temp-rectangle').geometry.coordinates[0];
            features.value = props.mapbox.map.queryRenderedFeatures(e);

            features.value = features.value.filter((feature: any) => {
                if (
                    feature.source === 'geo-records' ||
                    feature.source === 'geo-records-archive'
                ) {
                    let geometryType = feature.geometry.type;

                    if (geometryType === 'Point') {
                        let point = turf.point(feature.geometry.coordinates);
                        return turf.booleanPointInPolygon(
                            point,
                            turf.polygon([bbox]),
                        );
                    }

                    if (geometryType === 'LineString') {
                        let line = turf.lineString(
                            feature.geometry.coordinates,
                        );
                        return turf.booleanWithin(line, turf.polygon([bbox]));
                    }

                    if (geometryType === 'Polygon') {
                        let polygon = turf.polygon(
                            feature.geometry.coordinates,
                        );
                        return turf.booleanWithin(
                            polygon,
                            turf.polygon([bbox]),
                        );
                    }
                }
            });
        };

        const getGeoRecordsByFeature = (): void => {
            isLoading.value = true;
            features.value = features.value.filter((feature: any) => {
                return feature && recordSource.value.includes(feature.source);
            });

            if (features.value.length > 0) {
                features.value = [
                    ...new Map(
                        features.value.map((item) => [item.id, item]),
                    ).values(),
                ];
            }

            if (!isArchiveRecordSelected.value) {
                isRecordSelected.value = true;

                getMultipleGeoRecordDetails(features.value);

                isGeoRecordSelected.value = true;
                isArchiveRecordSelected.value = false;
            } else if (!isGeoRecordSelected.value) {
                isRecordSelected.value = true;

                getMultipleArchiveGeoRecordDetails(features.value);
                isArchiveRecordSelected.value = true;
                isGeoRecordSelected.value = false;
            }

            isLoading.value = false;
        };

        const handleRecordSource = (): void => {
            if (isArchiveRecordSelected.value) {
                recordSource.value = [
                    'geo-records-archive',
                    'geo-records-archive-extrusion',
                ];
                isGeoRecordSelected.value = false;
            } else {
                recordSource.value = ['geo-records', 'geo-records-extrusion'];
                isGeoRecordSelected.value = true;
            }
        };

        const getArchiveGeoRecordDetails = async (
            features: any,
        ): Promise<void> => {
            features.map(async (feature) => {
                if (
                    !geoRecordsArray.value.some((archiveGeoRecord) => {
                        return archiveGeoRecord.id === feature.id;
                    })
                ) {
                    isLoading.value = true;

                    let archiveGeoRecord = await getArchiveGeoRecord(
                        feature.id,
                    );

                    geoRecordsArray.value.push(archiveGeoRecord);

                    selectedGeoRecordNames.value.push(feature.properties);

                    isGeoRecordListCreated.value = true;
                    isMultipleGeomSelected.value = true;

                    initMultiGeoRecordSource(
                        props.mapbox.map,
                        geoRecordsArray.value,
                    );

                    isLoading.value = false;
                }
            });
        };

        const getMultipleArchiveGeoRecordDetails = async (
            features: any,
        ): Promise<void> => {
            let geoRecordIds: number[] = [];

            for (const feature of features) {
                if (
                    !geoRecordsArray.value.some(
                        (geoRecord) => geoRecord.id === feature.id,
                    )
                ) {
                    geoRecordIds.push(feature.id);
                }
            }

            let multiGeoRecords = await getMultipleArchiveGeoRecords(
                geoRecordIds,
            );
            multiGeoRecords.forEach((geoRecord) => {
                isModifyAllowed.value = checkIsModified(geoRecord);
                geoRecordsArray.value.push(geoRecord);
            });

            for (const feature of features) {
                selectedGeoRecordNames.value.push(feature.properties);

                isGeoRecordListCreated.value = true;
                isMultipleGeomSelected.value = true;

                initMultiGeoRecordSource(
                    props.mapbox.map,
                    geoRecordsArray.value,
                );
            }

            isLoading.value = false;
        };

        const onToolUnmounted = (): void => {
            props.mapbox.draw.changeMode('simple_select');
            props.mapbox.map.off('click', onMultiGeoRecordClick);

            turnOffFreeDrawMode();
            clearGeoRecordSelection();
        };

        const onToolMounted = async (): Promise<void> => {
            changeDrawMode();
            categorySummaries.value = categoriesToArray(
                await getGeoRecordCategorySummaryByRole(),
            );
        };

        const toggleIsLoading = (value: boolean) => {
            isLoading.value = value;
        };

        // lifecycle
        onMounted(() => {
            onToolMounted();
        });

        onUnmounted(() => {
            onToolUnmounted();
        });

        return {
            isLoading,
            onTabChange,
            saveGeoRecords,
            isModifyAllowed,
            geoRecordsArray,
            isGeomModificationActivated,
            selectedGeoRecordNames,
            isMultipleGeomSelected,
            clearGeoRecordSelection,
            changeDrawMode,
            isFreeDrawMode,
            Tools,
            handleRecordSource,
            isGeoRecordSelected,
            isArchiveRecordSelected,
            isRecordSelected,
            prepareGeoRecordFormData,
            activeTab,
            toggleIsLoading,
        };
    },
});
